import { createDisplayNumberFormat } from '../../utils/displayNumberFormat';
import i18n from 'i18next';
import { displayDigits } from '../../utils/displayDigits';
import { formatChartUnit } from './ChartUnitValue';
import { fmtMin } from './svgUtils';
import type { BarUnit } from './types';

/** Bright brand fill; text and line series keep their own readable ink roles. */
export const CHART_BAR_GREEN = 'var(--chart-bar-fill)';
export function formatChartDate(value: string | number, locale = i18n.resolvedLanguage ?? i18n.language): string {
  const date = typeof value === 'number' ? new Date(value) : /^\d{4}-\d{2}-\d{2}/.test(value) ? new Date(value.slice(0, 10) + 'T00:00:00Z') : null;
  if (!date || !Number.isFinite(date.getTime())) return displayDigits(value, locale);
  const parts = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }).formatToParts(date);
  const day = parts.find(part => part.type === 'day')?.value ?? '';
  const month = parts.find(part => part.type === 'month')?.value ?? '';
  return /^fa(?:-|$)/i.test(locale) ? formatChartUnit(day, month, locale) : new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(date);
}
export function formatChartBarValue(value: number, unit: BarUnit): string {
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const number = unit === 'time' ? displayDigits(fmtMin(value), locale) : createDisplayNumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
  const label = unit === 'number' ? '' : i18n.t(`charts.units.${unit === 'time' ? 'time' : unit}`);
  return label ? formatChartUnit(number, label, locale) : number;
}
export function chartAxisMaximum(values: number[]): number {
  const target = Math.max(1, ...values.filter(Number.isFinite)) * 1.12 / 3;
  const magnitude = 10 ** Math.floor(Math.log10(target));
  const step = [1, 2, 2.5, 5, 10].find(value => value * magnitude >= target) ?? 10;
  return Math.max(3, Math.ceil(step * magnitude)) * 3;
}

/** Round time axes to useful five/fifteen-minute or whole-hour steps. Values are hours. */
export function chartTimeAxisMaximum(hours: number[], intervals = 3): number {
  const target = Math.max(0, ...hours.filter(Number.isFinite)) * 60 * 1.12 / intervals;
  // Four-interval zone plots use finer steps so a three-hour stack doesn't
  // acquire a six-hour ceiling. Keep the existing scale for other time charts.
  const step = intervals === 4
    ? (target >= 120 ? 15 : 5)
    : (target >= 60 ? 60 : target >= 15 ? 15 : 5);
  return Math.max(step, Math.ceil(target / step) * step) * intervals / 60;
}
export function formatChartZoneLabel(label: string): string {
  const range = label.match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)\s*%/);
  const locale = i18n.resolvedLanguage ?? i18n.language;
  if (range) return formatChartUnit(displayDigits(`${range[1]}–${range[2]}`, locale), '%', locale);
  return displayDigits(label.replace(/^(?:Zone\s*|Z)(\d+)$/i, (_, number) => i18n.t('athleteSettings.powerEditor.zone', { number })), locale);
}
